import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, Trash2, Plus, Loader2, ImageIcon, Save, GripVertical } from 'lucide-react';

export const AdminHomepagePage = () => {
  const { api, token } = useAuth();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const bannerInputRef = useRef(null);
  const featureInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get('/site-settings');
      setSettings(res.data);
    } catch {
      toast.error('Failed to load site settings');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/upload?folder=homepage`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd
    });
    if (!res.ok) throw new Error('Upload failed');
    const data = await res.json();
    return `${API.replace(/\/api$/, '')}/api/files/${data.path}`;
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setUploading('banner');
    try {
      const url = await uploadFile(file);
      setSettings(prev => ({ ...prev, banner_slides: [...(prev.banner_slides || []), { url, alt: file.name.replace(/\.[^.]+$/, '') }] }));
      toast.success('Banner slide added');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(null); }
  };

  const handleFeatureUpload = async (file) => {
    if (!file) return;
    setUploading('feature');
    try {
      const url = await uploadFile(file);
      setSettings(prev => ({ ...prev, feature_photo: { ...(prev.feature_photo || {}), url } }));
      toast.success('Feature photo updated');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(null); }
  };

  const handleGalleryUpload = async (file) => {
    if (!file) return;
    setUploading('gallery');
    try {
      const url = await uploadFile(file);
      setSettings(prev => ({ ...prev, gallery_photos: [...(prev.gallery_photos || []), { url, caption: file.name.replace(/\.[^.]+$/, '') }] }));
      toast.success('Gallery photo added');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(null); }
  };

  const moveSlide = (arr, i, dir) => {
    const a = [...arr];
    const j = i + dir;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]];
    return a;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api().put('/site-settings', {
        slider_duration_seconds: settings.slider_duration_seconds,
        banner_slides: settings.banner_slides,
        feature_photo: settings.feature_photo,
        gallery_photos: settings.gallery_photos
      });
      toast.success('Homepage saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" /></div>;
  }

  return (
    <div data-testid="admin-homepage-page" className="space-y-6 md:space-y-8 animate-fade-in pb-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sticky top-0 bg-background pb-3 border-b border-zinc-900 z-10">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase tracking-wide">Homepage Editor</h1>
          <p className="text-sm text-muted-foreground mt-1">Edit banner slides, featured photo, gallery, and slider duration.</p>
        </div>
        <Button data-testid="save-homepage-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>

      {/* Slider duration */}
      <section className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 md:p-6 space-y-3">
        <h2 className="text-lg md:text-xl font-heading font-bold uppercase">Slider Timing</h2>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-zinc-300">Seconds per slide:</Label>
          <Input
            type="number"
            min={2}
            max={60}
            value={settings.slider_duration_seconds}
            onChange={e => setSettings({ ...settings, slider_duration_seconds: parseInt(e.target.value) || 5 })}
            className="w-24 bg-zinc-800 border-zinc-700"
            data-testid="slider-duration-input"
          />
          <span className="text-xs text-zinc-500">(2–60)</span>
        </div>
      </section>

      {/* Banner slides */}
      <section className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-lg md:text-xl font-heading font-bold uppercase">Banner Slides ({settings.banner_slides?.length || 0})</h2>
          <div>
            <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleBannerUpload(e.target.files?.[0])} data-testid="banner-file-input" />
            <Button onClick={() => bannerInputRef.current?.click()} disabled={uploading === 'banner'} data-testid="add-banner-slide-btn" className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700">
              {uploading === 'banner' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload Slide
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(settings.banner_slides || []).map((s, i) => (
            <div key={i} data-testid={`banner-slide-admin-${i}`} className="relative group bg-zinc-950 border border-zinc-800 rounded overflow-hidden">
              <img src={s.url} alt={s.alt || ''} className="w-full h-32 object-cover" />
              <div className="p-2 space-y-2">
                <Input
                  value={s.alt || ''}
                  onChange={e => {
                    const slides = [...settings.banner_slides];
                    slides[i] = { ...slides[i], alt: e.target.value };
                    setSettings({ ...settings, banner_slides: slides });
                  }}
                  placeholder="Alt text"
                  className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                />
                <div className="flex items-center justify-between gap-1">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={i === 0} onClick={() => setSettings({ ...settings, banner_slides: moveSlide(settings.banner_slides, i, -1) })} className="h-7 px-2 border-zinc-700 text-xs">↑</Button>
                    <Button size="sm" variant="outline" disabled={i === settings.banner_slides.length - 1} onClick={() => setSettings({ ...settings, banner_slides: moveSlide(settings.banner_slides, i, 1) })} className="h-7 px-2 border-zinc-700 text-xs">↓</Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSettings({ ...settings, banner_slides: settings.banner_slides.filter((_, j) => j !== i) })} className="h-7 px-2 border-red-900 text-red-400 hover:bg-red-900/20">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature photo */}
      <section className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-lg md:text-xl font-heading font-bold uppercase">Featured Photo (Hero)</h2>
          <div>
            <input ref={featureInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleFeatureUpload(e.target.files?.[0])} data-testid="feature-file-input" />
            <Button onClick={() => featureInputRef.current?.click()} disabled={uploading === 'feature'} data-testid="upload-feature-btn" className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700">
              {uploading === 'feature' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Replace Photo
            </Button>
          </div>
        </div>
        {settings.feature_photo?.url && (
          <img src={settings.feature_photo.url} alt="" className="w-full max-w-2xl h-48 object-cover rounded border border-zinc-800" />
        )}
      </section>

      {/* Gallery photos */}
      <section className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-lg md:text-xl font-heading font-bold uppercase">Photo Gallery ({settings.gallery_photos?.length || 0})</h2>
          <div>
            <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleGalleryUpload(e.target.files?.[0])} data-testid="gallery-file-input" />
            <Button onClick={() => galleryInputRef.current?.click()} disabled={uploading === 'gallery'} data-testid="add-gallery-photo-btn" className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700">
              {uploading === 'gallery' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload Photo
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(settings.gallery_photos || []).map((p, i) => (
            <div key={i} data-testid={`gallery-admin-${i}`} className="bg-zinc-950 border border-zinc-800 rounded overflow-hidden">
              <img src={p.url} alt={p.caption || ''} className="w-full h-28 object-cover" />
              <div className="p-2 space-y-1.5">
                <Input
                  value={p.caption || ''}
                  onChange={e => {
                    const g = [...settings.gallery_photos];
                    g[i] = { ...g[i], caption: e.target.value };
                    setSettings({ ...settings, gallery_photos: g });
                  }}
                  placeholder="Caption"
                  className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                />
                <Button size="sm" variant="outline" onClick={() => setSettings({ ...settings, gallery_photos: settings.gallery_photos.filter((_, j) => j !== i) })} className="w-full h-7 border-red-900 text-red-400 hover:bg-red-900/20">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
